import React, { useState, useEffect } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import apiService from '../../../../services/api';
import { formatDateTime, formatItemType } from './adminExtensionsHelpers';
import { ApproveBtn, ErrMsg, TypeChip } from './sharedButtons.styles';
import {
  CancelBtn, ConfirmDenyBtn, HighlightText, HintText,
  HistoryBox, HistoryEmpty, HistoryItem, HistoryItemTop, HistoryList, HistoryMeta,
  HistoryTitle, Input, Label, Modal, ModalBody, ModalFoot,
  ModalHead, ModalIconBadge, Overlay, PresetChip, PresetRow, Textarea,
} from './ExtensionModals.styles';

// ApproveModal + DenyModal — the approve/deny confirmation dialogs (with the
// grant-history box). Extracted VERBATIM from AdminExtensions.

function ApproveModal({ open, onClose, onConfirm, request }) {
  const [hours, setHours] = useState(48);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setHours(48);
    setNote('');
    setErr('');
    setHistory([]);
    setHistoryErr('');

    if (!request?.student_id || !request?.course_id) return;
    let cancelled = false;
    setHistoryLoading(true);
    apiService.getExtensionHistory({
      studentId: request.student_id,
      courseId: request.course_id,
      excludeId: request.id,
    })
      .then((res) => {
        if (!cancelled) setHistory(res?.history || []);
      })
      .catch((e) => {
        if (!cancelled) setHistoryErr(e?.data?.message || e?.message || 'Failed to load extension history.');
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, request]);
  if (!open) return null;

  const submit = async () => {
    setErr('');
    const n = Number(hours);
    if (!Number.isFinite(n) || n <= 0) { setErr('Enter a positive number of hours.'); return; }
    setSubmitting(true);
    try { await onConfirm({ durationHours: n, note: note.trim() || undefined }); }
    catch (e) { setErr(e?.errorData?.message || e?.message || 'Failed to approve.'); }
    finally { setSubmitting(false); }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <ModalHead>
          <ModalIconBadge $tint="rgba(107, 142, 127, 0.16)" $color="#466B5E"><FiCheck size={16} /></ModalIconBadge>
          <h3>Approve Extension</h3>
        </ModalHead>
        <ModalBody>
          <HintText>
            Grant <HighlightText>{request?.student_first_name} {request?.student_last_name}</HighlightText> access to{' '}
            <HighlightText>“{request?.item_title}”</HighlightText> for a limited time.
          </HintText>
          <Label htmlFor="approve-hours">Grant access duration (hours):</Label>
          <Input
            id="approve-hours" type="number" min="1" max="2160" value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
          <PresetRow>
            {[
              { label: '1 day',   h: 24  },
              { label: '2 days',  h: 48  },
              { label: '3 days',  h: 72  },
              { label: '1 week',  h: 168 },
              { label: '2 weeks', h: 336 },
              { label: '1 month', h: 720 },
            ].map((p) => (
              <PresetChip
                key={p.h}
                type="button"
                onClick={() => setHours(p.h)}
                $active={Number(hours) === p.h}
              >
                {p.label}
              </PresetChip>
            ))}
          </PresetRow>
          <div style={{ height: 14 }} />
          <Label htmlFor="approve-note">Note to student (optional)</Label>
          <Textarea id="approve-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          <HistoryBox>
            <HistoryTitle>Course Extension Request History</HistoryTitle>
            {historyLoading ? (
              <HistoryEmpty>Loading extension history…</HistoryEmpty>
            ) : historyErr ? (
              <HistoryEmpty>{historyErr}</HistoryEmpty>
            ) : history.length === 0 ? (
              <HistoryEmpty>No previously approved extensions for this student in this course.</HistoryEmpty>
            ) : (
              <HistoryList>
                {history.map((h) => (
                  <HistoryItem key={h.id}>
                    <HistoryItemTop>
                      <span>{h.item_title || `#${h.item_id}`}</span>
                      <TypeChip>{formatItemType(h.item_type, h.quiz_type)}</TypeChip>
                    </HistoryItemTop>
                    <HistoryMeta>
                      Approved {formatDateTime(h.reviewed_at || h.requested_at)}
                      {h.extension_duration_hours ? ` for ${h.extension_duration_hours} hours` : ''}
                      {h.access_expires_at ? ` · access until ${formatDateTime(h.access_expires_at)}` : ''}
                    </HistoryMeta>
                    {(h.review_note || h.justification) && (
                      <HistoryMeta>{h.review_note || h.justification}</HistoryMeta>
                    )}
                  </HistoryItem>
                ))}
              </HistoryList>
            )}
          </HistoryBox>
          {err && <ErrMsg>{err}</ErrMsg>}
        </ModalBody>
        <ModalFoot>
          <CancelBtn onClick={onClose} disabled={submitting}>Cancel</CancelBtn>
          <ApproveBtn onClick={submit} disabled={submitting}>
            {submitting ? 'Approving…' : 'Approve Extension'}
          </ApproveBtn>
        </ModalFoot>
      </Modal>
    </Overlay>
  );
}

function DenyModal({ open, onClose, onConfirm, request }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setNote(''); setErr(''); } }, [open]);
  if (!open) return null;

  const submit = async () => {
    setSubmitting(true); setErr('');
    try { await onConfirm({ note: note.trim() || undefined }); }
    catch (e) { setErr(e?.errorData?.message || e?.message || 'Failed to deny.'); }
    finally { setSubmitting(false); }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <ModalHead>
          <ModalIconBadge $tint="rgba(178, 125, 92, 0.14)" $color="#8C5638"><FiX size={16} /></ModalIconBadge>
          <h3>Deny Extension</h3>
        </ModalHead>
        <ModalBody>
          <HintText>
            Deny {request?.student_first_name} {request?.student_last_name}’s request for
            “{request?.item_title}”.
          </HintText>
          <Label htmlFor="deny-note">Reason for denial (optional)</Label>
          <Textarea id="deny-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          {err && <ErrMsg>{err}</ErrMsg>}
        </ModalBody>
        <ModalFoot>
          <CancelBtn onClick={onClose} disabled={submitting}>Cancel</CancelBtn>
          <ConfirmDenyBtn onClick={submit} disabled={submitting}>
            {submitting ? 'Denying…' : 'Deny Request'}
          </ConfirmDenyBtn>
        </ModalFoot>
      </Modal>
    </Overlay>
  );
}

export { ApproveModal, DenyModal };
